/**
 * TableDock - Custom compound header "rail" for map-overlay data tables.
 *
 * Provides ONE consistent 44px header across every table that overlays the map
 * (targets in view, port calls, transit reports) so operators don't relearn
 * each table's chrome. It is presentational only: it owns look, spacing, fixed
 * heights and the unified Actions-menu shell, while callers wire behaviour.
 *
 * Like DialogForm it is a *compound* component: the root renders the rail and
 * shares a little state (selection count, detached window) via context; callers
 * compose the three fixed zones with children. The zone order NEVER changes:
 *
 *   Identity (flex-none) -> State (elastic) -> Controls (flex-none)
 *
 *   <TableDock selectedCount={2} isDetached={false}>
 *     <TableDock.Identity icon={<PiBoatFill />} title="Targets in view" typeTag="Report" />
 *     <TableDock.State>
 *       <TableDock.FilterChip label="Vessel type: Cargo" onRemove={...} />
 *       <TableDock.SortChip label="Name" dir="asc" onClick={...} />
 *       <TableDock.SelectionChip count={2} onClear={...} />
 *       <TableDock.AddFilter onClick={...} />
 *     </TableDock.State>
 *     <TableDock.Controls>
 *       <TableDock.Actions resultCount={654}>
 *         <TableDock.ActionItem icon={<PiDownload />} onSelect={...}>Export to CSV</TableDock.ActionItem>
 *       </TableDock.Actions>
 *       <TableDock.PrimaryAction onClick={...}>Create</TableDock.PrimaryAction>
 *       <TableDock.Divider />
 *       <TableDock.Pager from={1} to={50} total={12904} matched={654} onPrev={...} onNext={...} />
 *       <TableDock.Divider />
 *       <TableDock.PopOut onClick={...} />
 *       <TableDock.Close onClick={...} />
 *     </TableDock.Controls>
 *   </TableDock>
 *
 * Each zone lives in its own file (Identity/State/Controls) and is stitched
 * together here into the `TableDock` namespace.
 */

import { TableDockRoot } from './Root';
import {
  TableDockDivider,
  TableDockIdentity,
  TableDockTypeTag,
} from './Identity';
import {
  chipVariants,
  TableDockAddFilter,
  TableDockFilterChip,
  TableDockSelectionChip,
  TableDockSortChip,
  TableDockState,
} from './State';
import {
  TableDockActionItem,
  TableDockActions,
  TableDockActionSeparator,
  TableDockClose,
  TableDockControls,
  TableDockPager,
  TableDockPopOut,
  TableDockPrimaryAction,
} from './Controls';

export type { TableDockProps } from './Root';
export type { IdentityProps as TableDockIdentityProps } from './Identity';
export type {
  FilterChipProps as TableDockFilterChipProps,
  SortChipProps as TableDockSortChipProps,
  SelectionChipProps as TableDockSelectionChipProps,
} from './State';
export type {
  ActionsProps as TableDockActionsProps,
  ActionItemProps as TableDockActionItemProps,
  PagerProps as TableDockPagerProps,
} from './Controls';

export { chipVariants as tableDockChipVariants };

export const TableDock = Object.assign(TableDockRoot, {
  Identity: TableDockIdentity,
  TypeTag: TableDockTypeTag,
  Divider: TableDockDivider,
  State: TableDockState,
  FilterChip: TableDockFilterChip,
  SortChip: TableDockSortChip,
  SelectionChip: TableDockSelectionChip,
  AddFilter: TableDockAddFilter,
  Controls: TableDockControls,
  Actions: TableDockActions,
  ActionItem: TableDockActionItem,
  ActionSeparator: TableDockActionSeparator,
  PrimaryAction: TableDockPrimaryAction,
  Pager: TableDockPager,
  PopOut: TableDockPopOut,
  Close: TableDockClose,
});
